import type {
  Consignment,
  ConsignmentWorkflowPhase,
  HelmInquiryStatus,
  Inquiry,
  InquiryWorkflowPhase,
  Profile,
  UpdateConsignmentRequest,
  UpdateInquiryRequest,
  WorkflowOperationalState,
} from './api/types'

/**
 * Quick edit from Helm's lists: an inquiry's or a consignment's owner, status
 * and due date, changed without opening the record. Everything else stays on
 * the full record.
 *
 * The transition tables below mirror src/workflows/state.ts, which the API
 * enforces. They are kept here, with relative imports only, so the API's tests
 * can import this file and prove the two agree (tests/helm_quick_edit.test.ts).
 */

const INQUIRY_PHASE_MOVES: Record<InquiryWorkflowPhase, InquiryWorkflowPhase[]> = {
  CAPTURED: ['REQUIREMENTS'],
  REQUIREMENTS: ['CAPTURED', 'SUPPLIER_QUOTES', 'EXECUTION'],
  SUPPLIER_QUOTES: ['REQUIREMENTS', 'EXECUTION'],
  EXECUTION: ['REQUIREMENTS', 'SUPPLIER_QUOTES', 'COMPLETED'],
  COMPLETED: ['EXECUTION'],
}

const CONSIGNMENT_PHASE_MOVES: Record<ConsignmentWorkflowPhase, ConsignmentWorkflowPhase[]> = {
  HANDOFF: ['PREPARATION'],
  PREPARATION: ['HANDOFF', 'IN_TRANSIT'],
  IN_TRANSIT: ['PREPARATION', 'CLEARANCE', 'DELIVERY'],
  CLEARANCE: ['IN_TRANSIT', 'DELIVERY'],
  DELIVERY: ['CLEARANCE', 'COMPLETED'],
  COMPLETED: ['DELIVERY'],
}

const STATE_MOVES: Record<WorkflowOperationalState, WorkflowOperationalState[]> = {
  ACTIVE: ['BLOCKED', 'COMPLETED', 'CANCELLED'],
  BLOCKED: ['ACTIVE', 'CANCELLED'],
  COMPLETED: ['ACTIVE'],
  CANCELLED: ['ACTIVE'],
}

function allows<T extends string>(moves: Record<T, T[]>, from: T, to: T) {
  return from === to || (moves[from]?.includes(to) ?? false)
}

/** A status a record can be given, and where it leaves the record's workflow. */
export interface StatusChoice {
  status: string
  workflow_phase: string
  operational_state: WorkflowOperationalState
}

/** Inquiry statuses in workflow order. Draft is where an inquiry starts, never a move. */
export const INQUIRY_STATUSES: HelmInquiryStatus[] = [
  'SUBMITTED',
  'QUALIFYING',
  'QUOTED',
  'ACTIVE',
  'BLOCKED',
  'COMPLETED',
  'CANCELLED',
]

/** The phase each status of work in progress stands for. */
const INQUIRY_STATUS_PHASE: Partial<Record<HelmInquiryStatus, InquiryWorkflowPhase>> = {
  SUBMITTED: 'CAPTURED',
  QUALIFYING: 'REQUIREMENTS',
  QUOTED: 'SUPPLIER_QUOTES',
  ACTIVE: 'EXECUTION',
}

/**
 * Where a status can leave an inquiry, best first. Blocked and Cancelled keep
 * the phase the work is in. Completed moves to the last phase when it can, and
 * otherwise closes the work where it stands, as the full record's State does.
 */
function inquiryTargets(
  status: HelmInquiryStatus,
  phase: InquiryWorkflowPhase,
): Array<[InquiryWorkflowPhase, WorkflowOperationalState]> {
  if (status === 'BLOCKED' || status === 'CANCELLED') return [[phase, status]]
  if (status === 'COMPLETED') {
    return [
      ['COMPLETED', 'COMPLETED'],
      [phase, 'COMPLETED'],
    ]
  }
  return [[INQUIRY_STATUS_PHASE[status]!, 'ACTIVE']]
}

/**
 * The statuses an inquiry can be given from where it is now, the current one
 * included. An inquiry's status follows from its phase and state, so a choice
 * carries both, and only moves the API accepts for both are offered.
 *
 * Cancelling and reopening are not status moves: they take a reason, close
 * tasks and stop alerts, so they go through their own actions. A cancelled
 * inquiry offers only where it is; one at work never offers Cancelled.
 */
export function inquiryStatusChoices(
  inquiry: Pick<Inquiry, 'status' | 'workflow_phase' | 'operational_state'>,
): StatusChoice[] {
  const current: StatusChoice = {
    status: inquiry.status,
    workflow_phase: inquiry.workflow_phase,
    operational_state: inquiry.operational_state,
  }
  if (inquiry.operational_state === 'CANCELLED') return [current]
  const choices = INQUIRY_STATUSES.flatMap((status): StatusChoice[] => {
    if (status === inquiry.status) return [current]
    if (status === 'CANCELLED') return []
    const target = inquiryTargets(status, inquiry.workflow_phase).find(
      ([phase, state]) =>
        allows(INQUIRY_PHASE_MOVES, inquiry.workflow_phase, phase) &&
        allows(STATE_MOVES, inquiry.operational_state, state),
    )
    return target ? [{ status, workflow_phase: target[0], operational_state: target[1] }] : []
  })
  return choices.includes(current) ? choices : [current, ...choices]
}

/**
 * Whether an inquiry can be cancelled: its work is active or blocked, and it
 * has not become a consignment. The API holds to the same.
 */
export function canCancelInquiry(
  inquiry: Pick<Inquiry, 'operational_state' | 'converted_consignment_id'>,
) {
  return (
    !inquiry.converted_consignment_id &&
    inquiry.operational_state !== 'CANCELLED' &&
    allows(STATE_MOVES, inquiry.operational_state, 'CANCELLED')
  )
}

/** Consignment statuses in the order a shipment moves through them. */
export const CONSIGNMENT_STATUSES = [
  'DRAFT',
  'AWAITING_INVOICE_FROM_FORWARDER',
  'AWAITING_PAYMENT',
  'PAYMENT_CONFIRMED',
  'IN_TRANSIT_DOMESTIC',
  'IN_TRANSIT_INTERNATIONAL',
  'ARRIVED',
  'CLEARING',
  'READY_FOR_PICKUP',
  'DELIVERED',
  'CLOSED',
] as const

export type ConsignmentStatus = (typeof CONSIGNMENT_STATUSES)[number]

const CONSIGNMENT_STATUS_PHASE: Record<string, ConsignmentWorkflowPhase> = {
  DRAFT: 'HANDOFF',
  AWAITING_INVOICE_FROM_FORWARDER: 'PREPARATION',
  AWAITING_PAYMENT: 'PREPARATION',
  PAYMENT_CONFIRMED: 'PREPARATION',
  IN_TRANSIT_DOMESTIC: 'IN_TRANSIT',
  IN_TRANSIT_INTERNATIONAL: 'IN_TRANSIT',
  ARRIVED: 'IN_TRANSIT',
  CLEARING: 'CLEARANCE',
  READY_FOR_PICKUP: 'DELIVERY',
  DELIVERED: 'DELIVERY',
  CLOSED: 'COMPLETED',
}

/**
 * The statuses a consignment can be given from where it is now, the current
 * one included. The API moves the phase (and the state, when a consignment is
 * closed or unblocked) along with the status, and refuses a jump its workflow
 * does not allow; those are left out.
 */
export function consignmentStatusChoices(
  consignment: Pick<
    Consignment,
    'status' | 'workflow_phase' | 'operational_state' | 'inquiry_stage'
  >,
): StatusChoice[] {
  const current: StatusChoice = {
    status: consignment.status,
    workflow_phase: consignment.workflow_phase,
    operational_state: consignment.operational_state,
  }
  const choices = CONSIGNMENT_STATUSES.flatMap((status): StatusChoice[] => {
    if (status === consignment.status) return [current]
    const phase = CONSIGNMENT_STATUS_PHASE[status]!
    const state: WorkflowOperationalState =
      status === 'CLOSED'
        ? 'COMPLETED'
        : consignment.inquiry_stage === 'BLOCKED'
          ? 'BLOCKED'
          : 'ACTIVE'
    return allows(CONSIGNMENT_PHASE_MOVES, consignment.workflow_phase, phase) &&
      allows(STATE_MOVES, consignment.operational_state, state)
      ? [{ status, workflow_phase: phase, operational_state: state }]
      : []
  })
  return choices.includes(current) ? choices : [current, ...choices]
}

/** Kampala keeps UTC+3 all year. */
const KAMPALA_OFFSET_MS = 3 * 60 * 60 * 1000
const DAY_MS = 24 * 60 * 60 * 1000
/** A date with no time of its own is due at the close of the Kampala working day. */
const CLOSE_OF_DAY_MS = 17 * 60 * 60 * 1000

/** A due timestamp as a date input shows it: its day in Kampala, or empty. */
export function dueDateInputValue(iso: string | null | undefined): string {
  const at = iso ? Date.parse(iso) : Number.NaN
  if (Number.isNaN(at)) return ''
  return new Date(at + KAMPALA_OFFSET_MS).toISOString().slice(0, 10)
}

/**
 * The due timestamp for a date picked in the list. A record already due at a
 * set time keeps that time on the new day; otherwise it is due by 17:00 in
 * Kampala. An empty date clears it.
 */
export function dueAtFromDateInput(date: string, previous: string | null | undefined) {
  const midnight = date ? Date.parse(`${date}T00:00:00.000Z`) - KAMPALA_OFFSET_MS : Number.NaN
  if (Number.isNaN(midnight)) return null
  const before = previous ? Date.parse(previous) : Number.NaN
  const timeOfDay = Number.isNaN(before)
    ? CLOSE_OF_DAY_MS
    : (((before + KAMPALA_OFFSET_MS) % DAY_MS) + DAY_MS) % DAY_MS
  return new Date(midnight + timeOfDay).toISOString()
}

/** What a quick edit reads from a row. */
export interface QuickEditRow {
  owner_user_id?: string | null
  status: string | null
  sla_due_at?: string | null
}

/** The quick edit form: empty strings mean no owner and no due date. */
export interface QuickEditForm {
  ownerUserId: string
  status: string
  dueDate: string
}

/** What a quick edit changed, as the row should show it straight away. */
export interface QuickEditChange {
  owner_user_id?: string | null
  status?: string
  workflow_phase?: string
  operational_state?: WorkflowOperationalState
  sla_due_at?: string | null
}

export function quickEditForm(row: QuickEditRow): QuickEditForm {
  return {
    ownerUserId: row.owner_user_id ?? '',
    status: row.status ?? '',
    dueDate: dueDateInputValue(row.sla_due_at),
  }
}

/** Only what the form changed; an untouched field is never sent. */
export function quickEditChange(
  row: QuickEditRow,
  form: QuickEditForm,
  choices: StatusChoice[],
): QuickEditChange {
  const change: QuickEditChange = {}
  const owner = form.ownerUserId || null
  if (owner !== (row.owner_user_id ?? null)) change.owner_user_id = owner
  const choice = choices.find((candidate) => candidate.status === form.status)
  if (choice && form.status !== row.status) {
    change.status = choice.status
    change.workflow_phase = choice.workflow_phase
    change.operational_state = choice.operational_state
  }
  if (form.dueDate !== dueDateInputValue(row.sla_due_at)) {
    change.sla_due_at = dueAtFromDateInput(form.dueDate, row.sla_due_at)
  }
  return change
}

/**
 * An inquiry's status is worked out from its phase and state, so the request
 * names those; the full record's workflow panel sends the same pair.
 */
export function inquiryQuickPatch(change: QuickEditChange): UpdateInquiryRequest {
  return {
    ...(change.owner_user_id !== undefined ? { owner_user_id: change.owner_user_id } : {}),
    ...(change.status !== undefined
      ? {
          workflow_phase: change.workflow_phase as InquiryWorkflowPhase,
          operational_state: change.operational_state,
        }
      : {}),
    ...(change.sla_due_at !== undefined ? { sla_due_at: change.sla_due_at } : {}),
  }
}

/** A consignment takes its status as is; the API moves the phase to match. */
export function consignmentQuickPatch(change: QuickEditChange): UpdateConsignmentRequest {
  return {
    ...(change.owner_user_id !== undefined ? { owner_user_id: change.owner_user_id } : {}),
    ...(change.status !== undefined ? { status: change.status as ConsignmentStatus } : {}),
    ...(change.sla_due_at !== undefined ? { sla_due_at: change.sla_due_at } : {}),
  }
}

/** The fields of a saved record that a quick edit can have changed. */
export function quickEditedFields<T extends QuickEditRow>(saved: T) {
  const record = saved as T & {
    workflow_phase?: string
    operational_state?: string
    updated_at?: string
  }
  return {
    owner_user_id: record.owner_user_id ?? null,
    status: record.status,
    workflow_phase: record.workflow_phase,
    operational_state: record.operational_state,
    sla_due_at: record.sla_due_at ?? null,
    updated_at: record.updated_at,
  } as unknown as Partial<T>
}

/** Quick edit is for admins, as the full records' editors are; the API enforces it too. */
export function canQuickEdit(profile: Pick<Profile, 'is_admin' | 'role'> | null | undefined) {
  return Boolean(profile?.is_admin || profile?.role === 'admin')
}

function errorField(error: unknown, field: 'status' | 'code' | 'message') {
  return error && typeof error === 'object' && field in error
    ? (error as Record<string, unknown>)[field]
    : undefined
}

/** Why a quick edit was not saved, in words a person can act on. */
export function quickEditErrorMessage(error: unknown): string {
  const status = Number(errorField(error, 'status')) || 0
  const code = String(errorField(error, 'code') ?? '')
  const message = String(errorField(error, 'message') ?? '')
  if (status === 401) return 'Your Helm session has ended. Sign in again, then try once more.'
  if (status === 403) return 'Only Helm admins can change this.'
  if (status === 404 && code === 'NOT_FOUND') {
    return 'Helm could not find this record any more. Refresh the list.'
  }
  if (status === 409 && code.startsWith('INVALID_')) {
    return 'That status change is not allowed from where this record is now. Refresh the list and try again.'
  }
  if (status === 400) return 'Helm did not accept that change. Check the values and try again.'
  if (status >= 500) return 'Helm could not save it just now. Try again in a moment.'
  if (error instanceof TypeError) {
    return 'Helm could not be reached. Check your connection and try again.'
  }
  return message || 'The change was not saved.'
}
